import { useState, useRef } from 'react';
import { api, uploadImage } from '../api';
import { toast } from './Toast';

const UNITS = ['', 'g', 'kg', 'ml', 'l', 'stuks', 'el', 'tl', 'mespunt', 'snuf', 'portie'];

function StarPicker({ value, onChange }) {
  return (
    <div className="star-picker">
      {[1,2,3,4,5].map(s => (
        <button
          key={s}
          type="button"
          className={`star-btn ${s <= value ? 'filled' : ''}`}
          onClick={() => onChange(s === value ? 0 : s)}
        >★</button>
      ))}
      {value > 0 && (
        <button type="button" onClick={() => onChange(0)} className="star-clear">✕</button>
      )}
    </div>
  );
}

export default function RecipeModal({ recipe, categories, onClose, onSaved }) {
  const isEdit = !!recipe;

  const [form, setForm] = useState({
    name: recipe?.name || '',
    image_url: recipe?.image_url || '',
    rating: recipe?.rating || 0,
    notes: recipe?.notes || '',
    prep_time: recipe?.prep_time || '',
    cook_time: recipe?.cook_time || '',
    servings: recipe?.servings || 4,
  });
  const [selectedCats, setSelectedCats] = useState(
    recipe?.categories?.map(c => c.id) || []
  );
  const [ingredients, setIngredients] = useState(
    recipe?.ingredients?.length
      ? recipe.ingredients.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit }))
      : [{ name: '', quantity: '', unit: '' }]
  );
  const [steps, setSteps] = useState(
    recipe?.steps?.length
      ? recipe.steps.map(s => ({ description: s.description }))
      : [{ description: '' }]
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm(f => ({ ...f, image_url: url }));
    } catch {
      toast('Fout bij uploaden afbeelding', 'error');
    }
    setUploading(false);
  };

  const toggleCat = (id) => {
    setSelectedCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const updateIngredient = (i, field, val) => {
    setIngredients(prev => prev.map((ing, idx) => idx === i ? { ...ing, [field]: val } : ing));
  };
  const addIngredient = () => setIngredients(prev => [...prev, { name: '', quantity: '', unit: '' }]);
  const removeIngredient = (i) => setIngredients(prev => prev.filter((_, idx) => idx !== i));

  const updateStep = (i, val) => {
    setSteps(prev => prev.map((s, idx) => idx === i ? { description: val } : s));
  };
  const addStep = () => setSteps(prev => [...prev, { description: '' }]);
  const removeStep = (i) => setSteps(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.name.trim()) { toast('Naam is verplicht', 'error'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        prep_time: parseInt(form.prep_time) || 0,
        cook_time: parseInt(form.cook_time) || 0,
        servings: parseInt(form.servings) || 4,
        category_ids: selectedCats,
        ingredients: ingredients.filter(i => i.name.trim()),
        steps: steps.filter(s => s.description.trim()),
      };
      if (isEdit) {
        await api.put(`/recipes/${recipe.id}`, body);
      } else {
        await api.post('/recipes', body);
      }
      onSaved();
    } catch {
      toast('Fout bij opslaan', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{isEdit ? 'Recept bewerken' : 'Nieuw recept'}</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Photo */}
          <div className="form-group">
            <label className="form-label">Foto</label>
            <div className="recipe-upload-area" onClick={() => fileRef.current?.click()}>
              {form.image_url ? (
                <img src={form.image_url} alt="recept" className="recipe-upload-preview" />
              ) : (
                <div className="recipe-upload-placeholder">
                  {uploading ? <span className="loading-spinner" /> : <><span style={{ fontSize: 28 }}>📷</span><span>Foto toevoegen</span></>}
                </div>
              )}
            </div>
            {form.image_url && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => fileRef.current?.click()}>
                Foto wijzigen
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImagePick} />
          </div>

          {/* Name */}
          <div className="form-group">
            <label className="form-label">Naam *</label>
            <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Naam van het recept..." />
          </div>

          {/* Rating */}
          <div className="form-group">
            <label className="form-label">Beoordeling</label>
            <StarPicker value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
          </div>

          {/* Categories */}
          {categories?.length > 0 && (
            <div className="form-group">
              <label className="form-label">Categorieën</label>
              <div className="recipe-cat-picker">
                {categories.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className={`filter-pill ${selectedCats.includes(c.id) ? 'active' : ''}`}
                    onClick={() => toggleCat(c.id)}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Time & servings */}
          <div className="form-row">
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Voorbereidingstijd (min)</label>
              <input className="form-input" type="number" min="0" inputMode="numeric" value={form.prep_time} onChange={e => setForm(f => ({ ...f, prep_time: e.target.value }))} placeholder="0" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Kooktijd (min)</label>
              <input className="form-input" type="number" min="0" inputMode="numeric" value={form.cook_time} onChange={e => setForm(f => ({ ...f, cook_time: e.target.value }))} placeholder="0" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Porties</label>
            <input className="form-input" type="number" min="1" inputMode="numeric" value={form.servings} onChange={e => setForm(f => ({ ...f, servings: e.target.value }))} style={{ maxWidth: 100 }} />
          </div>

          {/* Ingredients */}
          <div className="form-group">
            <label className="form-label">Ingrediënten</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ingredients.map((ing, i) => (
                <div key={i} className="ingredient-row">
                  <input
                    className="form-input"
                    placeholder="Hoeveelheid"
                    value={ing.quantity}
                    onChange={e => updateIngredient(i, 'quantity', e.target.value)}
                    style={{ width: 80, flexShrink: 0 }}
                  />
                  <select
                    className="form-select"
                    value={ing.unit}
                    onChange={e => updateIngredient(i, 'unit', e.target.value)}
                    style={{ width: 80, flexShrink: 0 }}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u || '—'}</option>)}
                  </select>
                  <input
                    className="form-input"
                    placeholder="Ingrediënt"
                    value={ing.name}
                    onChange={e => updateIngredient(i, 'name', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {ingredients.length > 1 && (
                    <button type="button" className="btn btn-danger btn-icon btn-sm" onClick={() => removeIngredient(i)}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-ghost btn-sm" onClick={addIngredient} style={{ alignSelf: 'flex-start' }}>
                + Ingrediënt toevoegen
              </button>
            </div>
          </div>

          {/* Steps */}
          <div className="form-group">
            <label className="form-label">Bereiding</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ice)', paddingTop: 12, minWidth: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                  <textarea
                    className="form-textarea"
                    placeholder={`Stap ${i + 1}...`}
                    value={step.description}
                    onChange={e => updateStep(i, e.target.value)}
                    style={{ flex: 1, minHeight: 56 }}
                  />
                  {steps.length > 1 && (
                    <button type="button" className="btn btn-danger btn-icon btn-sm" onClick={() => removeStep(i)} style={{ marginTop: 6 }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-ghost btn-sm" onClick={addStep} style={{ alignSelf: 'flex-start' }}>
                + Stap toevoegen
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">Notities</label>
            <textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Tips, variaties, opmerkingen..." />
          </div>

        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuleren</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="loading-spinner" /> : (isEdit ? 'Opslaan' : 'Toevoegen')}
          </button>
        </div>
      </div>
    </div>
  );
}
